import { useState, useCallback } from 'react';
import { Container, Nav } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  type TppCardSummary,
} from '../services/transitPassPool.service';
import TppPageModals from '../components/systems/transitPassPool/TppPageModals';
import TppZipGate from '../components/systems/transitPassPool/TppZipGate';
import TppListTab from '../components/systems/transitPassPool/TppListTab';
import TppCardManagementHub from '../components/systems/transitPassPool/TppCardManagementHub';
import TppPageHeader from '../components/systems/transitPassPool/TppPageHeader';
import TppFlashAlert from '../components/systems/transitPassPool/TppFlashAlert';
import {
  useTppPageMutations,
  type TppManageTab,
} from '../components/systems/transitPassPool/useTppPageMutations';
import { useTppCardList } from '../components/systems/transitPassPool/useTppCardList';
import { useTppFlashMessage } from '../components/systems/transitPassPool/useTppFlashMessage';
import { useTppMoneyFormat } from '../components/systems/transitPassPool/useTppMoneyFormat';
import { useTppPoolQueries } from '../components/systems/transitPassPool/useTppPoolQueries';
import { useTppProofViewer } from '../components/systems/transitPassPool/useTppProofViewer';
import { useTppZipSession } from '../components/systems/transitPassPool/useTppZipSession';
import { isTppProofFileAllowed } from '../services/systems/transit-pass-pool/tppProofFile';
import CollapsibleHelpSection from '../components/common/CollapsibleHelpSection';
import LoadingSpinner from '../components/common/LoadingSpinner';
import PageMeta from '../components/common/PageMeta';
import TransitPassPoolPageStructuredData from '../components/systems/transitPassPool/TransitPassPoolPageStructuredData';

const TPP_PAGE_SHELL_STYLE = { backgroundColor: '#f8f9fa', minHeight: '100vh' } as const;
const TPP_GATE_SHELL_STYLE = {
  background:
    'radial-gradient(ellipse 68% 48% at 50% 28%, #fafbfc 0%, transparent 70%), #f4f6f8',
  minHeight: '100vh',
} as const;

export default function TransitPassPool() {
  const { t } = useTranslation();
  const { user } = useAuth();

  const { flashMessage, flashVariant, setFlashMessage, setFlashError, clearFlash } =
    useTppFlashMessage();
  const [activeTab, setActiveTab] = useState<TppManageTab>('list');
  const [selected, setSelected] = useState<TppCardSummary | null>(null);

  const clearSelected = useCallback(() => setSelected(null), []);
  const resetSessionUi = useCallback(() => {
    setSelected(null);
    setActiveTab('list');
  }, []);
  const { zipConnected, zipConn, handleSwitchZip, handleDownloadZip, downloadPending } =
    useTppZipSession(resetSessionUi, { setFlashMessage, setFlashError });
  const { poolKey, accessQuery, settingsQuery } = useTppPoolQueries(zipConnected);

  const {
    proofViewer,
    proofViewerLoading,
    proofViewError,
    openProofViewer,
    closeProofViewer,
  } = useTppProofViewer();

  const list = useTppCardList({
    zipConnected,
    poolKey,
    zipSessionKey: zipConn?.fileName ?? null,
    accessTotalPoolCards: accessQuery.data?.totalPoolCards,
    selected,
    setSelected,
  });

  const mut = useTppPageMutations({
    poolKey,
    selected,
    setSelected,
    setActiveTab,
    setFlashMessage,
    setFlashError,
  });

  const {
    money,
    zeroBalanceLabel,
    prefix: amountPrefix,
    suffix: amountSuffix,
    formatAbsAmount,
  } = useTppMoneyFormat(settingsQuery.data);
  const modalCloseLabel = t('common.close');

  if (zipConnected && accessQuery.isLoading) {
    return (
      <div className="py-5" style={TPP_PAGE_SHELL_STYLE}>
        <Container>
          <LoadingSpinner
            className="mt-5"
            text={t('common.loading')}
          />
        </Container>
      </div>
    );
  }

  const poolCardCount =
    accessQuery.data?.totalPoolCards ?? list.cardsQuery.data?.total ?? 0;

  return (
    <div
      id="transit-pass-pool-page"
      className={`py-4${!zipConnected ? ' tpp-page--gate' : ''}`}
      style={!zipConnected ? TPP_GATE_SHELL_STYLE : TPP_PAGE_SHELL_STYLE}
    >
      <PageMeta
        titleKey="tpp.page_meta_title"
        descriptionKey="tpp.page_meta_description"
        keywordsKey="tpp.page_meta_keywords"
        path="transit-pass-pool"
        llmsDescribedBy
      />
      <TransitPassPoolPageStructuredData />
      <Container className={!zipConnected ? 'tpp-gate-page-container' : undefined}>
        <TppPageHeader
          zipConnected={zipConnected}
          zipConn={zipConn}
          showGuestChip={!user && !zipConnected}
          onSwitchZip={handleSwitchZip}
          onDownloadZip={handleDownloadZip}
          downloadPending={downloadPending}
        />

        <TppFlashAlert
          message={flashMessage}
          variant={flashVariant}
          onClose={clearFlash}
        />

        {!zipConnected && (
          <TppZipGate onConnected={clearFlash} onError={setFlashError} />
        )}

        {!zipConnected && (
          <div className="tpp-gate-help">
            <CollapsibleHelpSection
              titleKey="tpp.help_title_beginner"
              descriptionKey="tpp.guide_first_visit_body"
              stepsKey={[
                'tpp.guide_flow_1',
                'tpp.guide_flow_2',
                'tpp.guide_flow_3',
                'tpp.guide_flow_4',
              ]}
              storageKey="tpp-beginner-guide"
              collapsedButtonKey="tpp.help_collapsed_button"
            />
          </div>
        )}

        {zipConnected && (
          <>
            <Nav
              variant="tabs"
              className="mb-3"
              activeKey={activeTab}
              onSelect={(k) => setActiveTab((k as TppManageTab) || 'list')}
            >
              <Nav.Item>
                <Nav.Link eventKey="list">{t('tpp.tab_card_list')}</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="setup">{t('tpp.setup_footer_title')}</Nav.Link>
              </Nav.Item>
            </Nav>

            {activeTab === 'list' && (
              <TppListTab
                list={list}
                poolKey={poolKey}
                money={money}
                amountPrefix={amountPrefix}
                amountSuffix={amountSuffix}
                formatAbsAmount={formatAbsAmount}
                zeroBalanceLabel={zeroBalanceLabel}
                ops={{
                  onReload: () => {
                    void mut.refreshTpp();
                  },
                  onRegister: mut.openCreateCardModal,
                  onBorrow: mut.openBorrowFor,
                  onReturn: mut.openReturnFor,
                  onCharge: mut.openChargeFor,
                  onEditCard: mut.openEditCardModal,
                  onEditEvent: mut.openEditEvent,
                  setActiveTabSetup: () => setActiveTab('setup'),
                }}
                proof={{
                  onUploadProof: (eventId, file) => {
                    if (!isTppProofFileAllowed(file)) {
                      setFlashError(t('tpp.proof_type_rejected'));
                      return;
                    }
                    mut.uploadProofMut.mutate({ eventId, file });
                  },
                  uploadProofPending: mut.uploadProofMut.isPending,
                  uploadProofEventId: mut.uploadProofMut.variables?.eventId,
                  uploadProofError: mut.uploadProofMut.isError,
                  onViewProof: (eventId) => void openProofViewer(eventId),
                  proofViewerLoading,
                }}
              />
            )}

            {activeTab === 'setup' && (
              <TppCardManagementHub
                totalPoolCards={poolCardCount}
                onRegister={mut.openCreateCardModal}
                onImport={mut.openImportExcelModal}
                onSettings={mut.openPoolSettingsModal}
                onExport={mut.openExportModal}
              />
            )}

            <TppPageModals
              closeLabel={modalCloseLabel}
              money={money}
              zeroBalanceLabel={zeroBalanceLabel}
              poolKey={poolKey}
              selected={selected}
              otherCheckedOutCount={list.otherCheckedOutTotal}
              poolSettingsSeed={settingsQuery.data}
              mut={mut}
              proof={{
                open: proofViewerLoading || !!proofViewer || !!proofViewError,
                loading: proofViewerLoading,
                error: proofViewError,
                fileUrl: proofViewer?.url ?? null,
                displayKind: proofViewer?.displayKind ?? null,
                fileName: proofViewer?.fileName ?? null,
                onHide: closeProofViewer,
              }}
            />
          </>
        )}
      </Container>
    </div>
  );
}
